// useGalleryMasonryLayout.js

import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsMobile } from 'hooks/useDeviceDetect';
import resolveMediaAspectRatio from 'views/gallery/utils/resolveMediaAspectRatio';

const GAP = 12;
const ROW_HEIGHT = 200;
const MIN_COLS = 2;
const MAX_COLS = Infinity;
const COL_WIDTH = 240;
const GRID_PADDING = 4;
const MAX_MEDIA_ROW_SPAN = 6;
const SMALL_MEDIA_SET_COLS = 3;

const useGalleryMasonryLayout = ({
    displayedNodes,
    isMediaType,
    typeDef,
    getNodeFieldCount,
}) => {
    const isMobile = useIsMobile();

    const containerRef = useRef(null);
    const [layout, setLayout] = useState([]);
    const [numCols, setNumCols] = useState(MIN_COLS);
    const [containerWidth, setContainerWidth] = useState(0);
    const [aspectRatios, setAspectRatios] = useState({});

    const getColumnWidth = useCallback((resolvedCols = numCols) => {
        const cols = Math.max(1, resolvedCols);
        const totalGap = GAP * Math.max(0, cols - 1);
        const totalPadding = GRID_PADDING * 2;
        const available = Math.max(1, (containerWidth || 0) - totalGap - totalPadding);
        return Math.max(1, available / cols);
    }, [numCols, containerWidth]);

    const getSpanFromNode = useCallback((node, resolvedCols = numCols) => {
        const nodeTypeKey = String(node?.type || typeDef?.key || '').toUpperCase();
        if (nodeTypeKey === 'PHOTO' || nodeTypeKey === 'VIDEO' || nodeTypeKey === 'SKETCH') {
            const nodeId = node?.key || null;
            const mappedRatio = nodeId ? aspectRatios[nodeId] : null;
            const aspect = mappedRatio || resolveMediaAspectRatio(node);
            const columnWidth = getColumnWidth(resolvedCols);
            const estimatedHeight = columnWidth / Math.max(0.05, aspect);
            const rawSpan = Math.round(estimatedHeight / Math.max(1, ROW_HEIGHT));
            const rowSpan = Math.max(1, Math.min(MAX_MEDIA_ROW_SPAN, rawSpan || 1));
            return { colSpan: 1, rowSpan };
        }

        const fieldCount = getNodeFieldCount(node);
        if (fieldCount <= 0) return { colSpan: 1, rowSpan: 1 };
        if (fieldCount <= 2) return { colSpan: 1, rowSpan: 2 };
        if (fieldCount <= 4) return { colSpan: 1, rowSpan: 3 };
        return { colSpan: 1, rowSpan: 4 };
    }, [aspectRatios, getColumnWidth, getNodeFieldCount, numCols, typeDef?.key]);

    const calculateColumns = useCallback((width) => {
        const safeMin = Math.max(1, MIN_COLS);
        const estimated = Math.floor(width / Math.max(1, COL_WIDTH));
        const clamped = Math.min(MAX_COLS, Math.max(safeMin, estimated || safeMin));
        if (isMobile) return Math.max(2, clamped);
        return clamped;
    }, [isMobile]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;

        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const width = entry?.contentRect?.width || 0;
                const next = calculateColumns(width);
                setContainerWidth((prev) => (prev === width ? prev : width));
                setNumCols((prev) => (prev === next ? prev : next));
            }
        });

        ro.observe(el);
        return () => ro.disconnect();
    }, [calculateColumns]);

    useEffect(() => {
        const hasSmallMediaSet = isMediaType
            && displayedNodes?.length > 0
            && displayedNodes.length <= SMALL_MEDIA_SET_COLS;
        const resolvedCols = hasSmallMediaSet ? SMALL_MEDIA_SET_COLS : Math.max(1, numCols);

        if (!isMediaType) {
            setLayout([]);
            return;
        }

        if (!displayedNodes || displayedNodes.length === 0) {
            setLayout([]);
            return;
        }

        const occupancy = [];
        const placedItems = [];

        const isFree = (r, c, rowSpan, colSpan) => {
            for (let i = r; i < r + rowSpan; i++) {
                for (let j = c; j < c + colSpan; j++) {
                    if (j >= resolvedCols) return false;
                    if (!occupancy[i]) occupancy[i] = Array(resolvedCols).fill(false);
                    if (occupancy[i][j]) return false;
                }
            }
            return true;
        };

        const markOccupied = (r, c, rowSpan, colSpan) => {
            for (let i = r; i < r + rowSpan; i++) {
                if (!occupancy[i]) occupancy[i] = Array(resolvedCols).fill(false);
                for (let j = c; j < c + colSpan; j++) {
                    occupancy[i][j] = true;
                }
            }
        };

        for (const item of displayedNodes) {
            let { rowSpan, colSpan } = getSpanFromNode(item, resolvedCols);
            colSpan = Math.max(1, Math.min(colSpan, resolvedCols));
            rowSpan = Math.max(1, rowSpan);
            let placed = false;
            let row = 0;
            while (!placed) {
                for (let col = 0; col <= resolvedCols - colSpan; col++) {
                    if (isFree(row, col, rowSpan, colSpan)) {
                        markOccupied(row, col, rowSpan, colSpan);
                        placedItems.push({
                            ...item,
                            gridColumnStart: col + 1,
                            gridColumnEnd: col + 1 + colSpan,
                            gridRowStart: row + 1,
                            gridRowEnd: row + 1 + rowSpan,
                        });
                        placed = true;
                        break;
                    }
                }
                if (!placed) row += 1;
            }
        }

        setLayout(placedItems);
    }, [displayedNodes, getSpanFromNode, isMediaType, numCols]);

    const renderedNumCols = isMediaType
        && displayedNodes?.length > 0
        && displayedNodes.length <= SMALL_MEDIA_SET_COLS
        ? SMALL_MEDIA_SET_COLS
        : numCols;

    const handleAspectRatio = useCallback((node, ratio) => {
        const nodeId = node?.key || null;
        if (!nodeId || !ratio || Number.isNaN(ratio)) return;
        setAspectRatios((prev) => {
            if (prev[nodeId] === ratio) return prev;
            return { ...prev, [nodeId]: ratio };
        });
    }, []);

    return {
        containerRef,
        layout,
        numCols: renderedNumCols,
        handleAspectRatio,
        gap: GAP,
        rowHeight: ROW_HEIGHT,
        minCols: MIN_COLS,
        maxCols: MAX_COLS,
        colWidth: COL_WIDTH,
    };
};

export default useGalleryMasonryLayout;
